/** Human file size. One implementation — BugReportModal, ClientView and Scheduled all
 *  rendered their own, and only one of the three ever switched to MB. */
/* An RFC 5322 From is sometimes a bare `<addr@x.com>` with no display name.
   Stripping the angle-bracket address then leaves an EMPTY string, so a run of
   outbound messages shows no sender at all. Fall back to the address, prettified
   the way the backend derives a sign-off from one (`email_generation.py:579`):
   local part, dots to spaces, title case. That is a presentation rule the app
   already applies, not invented data — the address is right there in the header. */
export function senderName(raw: string): string {
  const named = (raw || '').replace(/\s*<[^>]+>\s*/, '').replace(/^["']|["']$/g, '').trim();
  if (named) return named;
  const addr = ((raw || '').match(/[\w.+-]+@[\w.-]+/) || [''])[0];
  if (!addr) return '';
  return addr.split('@')[0].replace(/[._-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024) return `${Math.round(bytes)} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function isPdf(name: string): boolean {
  return name.toLowerCase().endsWith('.pdf');
}

/** SVG path data for a file row's outline icon, keyed by extension; the `<svg>` wrapper sizes it. */
export function fileIconPath(name: string): string {
  const ext = name.split('.').pop()?.toLowerCase() || '';
  // image: framed picture with a horizon
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext))
    return 'M3 3h18v18H3zM3 15l5-5 4 4 3-3 6 6';
  // spreadsheet: a grid
  if (['xls', 'xlsx', 'csv'].includes(ext))
    return 'M3 3h18v18H3zM3 9h18M3 15h18M9 3v18M15 3v18';
  // any document: a page with a folded corner, plus rules for text-bearing types
  const page = 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6';
  if (['pdf', 'doc', 'docx', 'md', 'txt', 'rtf'].includes(ext))
    return page + 'M8 13h8M8 17h5';
  return page;
}

/** A calendar day in the VIEWER's timezone, `YYYY-MM-DD`.
 *  ⚠️ NOT `toISOString().slice(0,10)` — that is the UTC day, which is a different date for
 *  ~30% of the day outside UTC (late west of it, early east of it). The server stores
 *  follow-ups as bare days, so what gets written must be the day the user means. */
export function localDay(d: Date = new Date()): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Parse a bare `YYYY-MM-DD` as LOCAL midnight; pass anything else through.
 *  ⚠️ `new Date('2026-04-11')` is UTC midnight and renders as the previous day west of
 *  UTC. Appending a time makes it local. (Noon works too — see ScoreChart.) */
export function parseDay(dateStr: string): Date {
  return new Date(/^\d{4}-\d{2}-\d{2}$/.test(dateStr) ? `${dateStr}T00:00` : dateStr);
}

/** Whole calendar days between a `YYYY-MM-DD` and today, both in the viewer's zone. */
export function daysSince(dateStr: string): number {
  const then = parseDay(dateStr);
  const now = new Date();
  then.setHours(0, 0, 0, 0);
  now.setHours(0, 0, 0, 0);
  return Math.round((now.getTime() - then.getTime()) / 86400000);
}
